import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styled from 'src/ui/library/styled';
import TextField from '@mui/material/TextField';
// import TextField from 'src/ui/library/TextInput';
import { List } from 'react-virtualized';
import ArrowDropDownIcon from '@mui/icons-material/KeyboardArrowDown';

const isEmptyDropdownValue = value => value === null || typeof value === 'undefined' || value === '';

const styles = (theme, props) => ({
  textField: {
    height: '48px',
    width: '100%',
    maxWidth: '300px',
    color: theme.colors.eyConfidentGrey,
    backgroundColor: theme.colors.eyGrey5,
    border: '1px solid transparent',
    overflow: 'hidden',
  },
  textFieldFocus: {
    border: `1px solid ${theme.colors.eyGrey6}`,
  },
  menu: {
    maxHeight: '260px',
    // left: '-8px',
    top: '1px',
    //maxWidth: '300px',
    //width: '100px',
  },
  menuPaper: {
    overflowX: props.virtualized ? 'hidden' : 'hidden',
    overflowY: props.virtualized ? 'hidden' : 'auto',
    border: '1px solid #C4C4CE',
    '& .MuiList-root': {
      paddingTop: 0,
      paddingBottom: 0,
    },
    '& .MuiMenuItem-root': {
      minHeight: '48px',
      paddingTop: 0,
      paddingBottom: 0,
      color: theme.colors.eyConfidentGrey,
      fontFamily: '"EYInterstate", sans-serif',
      fontSize: '1rem',
      lineHeight: '20px',
    },
    '& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover, & .MuiMenuItem-root.Mui-focusVisible': {
      // backgroundColor: '#E8F4FD',
      backgroundColor: 'rgba(0, 0, 0, 0.14)',
    },
    '& .MuiMenuItem-root:hover': {
      backgroundColor: theme.colors.eyGrey4,
    },
    '& .MuiMenuItem-root:focus': {
      backgroundColor: 'rgba(0, 0, 0, 0.08)',
    }
  },
  select: {
    color: theme.colors.eyConfidentGrey,
    fontFamily: '"EYInterstate", sans-serif',
    lineHeight: '20px',
    '&:focus': {
      backgroundColor: 'transparent',
    },
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
    width: 100,
  },
  selectIcon: {
    color: theme.colors.eyConfidentGrey,
    top: 'calc(50% - 19px)',
    right: '4px',
    transformOrigin: 'center',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  selectIconOpen: {
    transform: 'rotate(180deg)',
  },
  inputRoot: {
    paddingLeft: '7px',
    marginTop: '8px',
    fontFamily: '"EYInterstate", sans-serif',
  },
  formLabel: {
    color: theme.colors.lightGrey,
    paddingLeft: '8px',
    '& span': {
      color: theme.colors.red,
    },
  },
  formControl: {
    transform: 'translate(0, 14px) scale(1)',
    paddingLeft: '10px',
  },
  shrink: {
    transform: 'translate(0, 5px) scale(0.75)',
    marginBottom: '8px',
    color: theme.colors.eyGrey14,
    width: 'calc(100% + 100% * 0.20)', // due to the shrink transform the width needs to be accounted for
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  focused: {
    '&$focused': {
      color: theme.colors.eyGrey1,
    },
  },
});

class DropdownBase extends React.Component {
  static propTypes = {
    /** Classes injected by the styled wrapper. */
    classes: PropTypes.object.isRequired,
    /** ID attribute of the input element. */
    id: PropTypes.string,
    /** Classname to be applied to <TextField> */
    className: PropTypes.string,
    /** Dropdown options. */
    children: PropTypes.node,
    /** If `true`, the input will not have an underline. */
    disableUnderline: PropTypes.bool,
    /** If `true`, textfield will be in error state */
    hasError: PropTypes.bool,
    /** If `true`, Dropdown will render virtulized */
    virtualized: PropTypes.bool,
    /** Used in conjunction with `virtualized` allowing a user to render proper label */
    listOptions: PropTypes.array,
    /** Props applied to the input label. */
    InputLabelProps: PropTypes.object,
    /** Override or extend default styles */
    styles: PropTypes.object,
    /** Name attribute of the `input` element. Falls back to id when omitted. */
    name: PropTypes.string,
    /** Value of `input` element */
    value: PropTypes.any,
    /** Text used for input label */
    label: PropTypes.string,
    /** If `true`, the label is displayed as required. */
    required: PropTypes.any,
    /** Callback fired when value is changed */
    onChange: PropTypes.func,
    /** Custom selected value renderer for the MUI Select */
    renderValue: PropTypes.func,
  };

  static defaultProps = {
    virtualized: false,
  };

  state = {
    focused: false,
    menuOpen: false,
    index: null,
  };

  handleOnFocus = status => {
    this.setState({ focused: status });
  };

  handleMenuOpen = status => {
    this.setState({ menuOpen: status });
  };

  getVirtualizedValue = selectedIndex => {
    const { listOptions } = this.props;

    if (!listOptions) return selectedIndex;

    let displayVal = selectedIndex;

    listOptions.forEach(item => {
      if (item.lookUpBindValue === selectedIndex) displayVal = item.displayText;
    });

    return displayVal;
  };

  renderSingleRow = row => {
    const { key, index, style } = row;
    const { children } = this.props;

    return React.cloneElement(children[index], {
      key: key,
      style: {
        ...style,
        paddingTop: '0',
        paddingBottom: '0',
      },
      onClick: () => {
        this.props.onChange({
          target: {
            value: children[index].props.value,
          },
        });
        this.setState({ menuOpen: false });
      },
    });
  };

  render() {
    const {
      classes,
      id,
      name,
      className,
      disableUnderline,
      hasError,
      onChange,
      value,
      children,
      virtualized,
      InputLabelProps,
      renderValue,
      ...otherProps
    } = this.props;

    const textClassName = classNames(
      {
        [classes.textField]: true,
        [classes.textFieldFocus]: this.state.focused,
        [classes.hasError]: hasError,
      },
      className,
    );
    const dropdownValue = isEmptyDropdownValue(value) ? '' : value;
    const shouldShrinkLabel = this.state.focused || !isEmptyDropdownValue(dropdownValue);
    const dropdownChildren = children || [];
    const inputName = name || id;

    return (
      <TextField
        variant="standard"
        id={id}
        name={inputName}
        value={dropdownValue}
        className={textClassName}
        select
        SelectProps={{
          native: false,
          open: this.state.menuOpen,
          onFocus: this.handleOnFocus.bind(null, true),
          onBlur: this.handleOnFocus.bind(null, false),
          onOpen: this.handleMenuOpen.bind(null, true),
          onClose: this.handleMenuOpen.bind(null, false),
          IconComponent: ArrowDropDownIcon,
          renderValue: renderValue || (virtualized ? selected => this.getVirtualizedValue(selected) : undefined),
          classes: {
            select: classes.select,
            icon: classes.selectIcon,
            iconOpen: classes.selectIconOpen,
          },
          MenuProps: {
            className: classes.menu,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            classes: {
              paper: classes.menuPaper,
            },
            MenuListProps: {
              disablePadding: virtualized,
            },
          },
        }}
        InputLabelProps={{
          shrink: shouldShrinkLabel,
          classes: {
            formControl: classes.formControl,
            shrink: classes.shrink,
            root: classes.formLabel,
            focused: classes.focused,
          },
          ...InputLabelProps,
        }}
        InputProps={{
          disableUnderline,
          className: classes.inputRoot,
        }}
        onChange={!virtualized ? e => onChange(e, dropdownChildren) : undefined}
        {...otherProps}>
        {virtualized ? (
          <List width={299} height={160} rowCount={dropdownChildren.length} rowHeight={48} rowRenderer={this.renderSingleRow} />
        ) : (
          dropdownChildren
        )}
      </TextField>
    );
  }
}

export { DropdownBase };
export default styled(DropdownBase)(styles);
